import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useExpense } from "@/contexts/ExpenseContext";
import { Plus, Target, TrendingDown, TrendingUp } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useNavigate } from "react-router-dom";
import { colorOptions, getRandomColor } from "@/utils/colors";
import { endOfMonth, format, startOfMonth } from "date-fns";
import { CategoryCard, type CategoryCardData } from "@/components/categories/CategoryCard";
import { AddCategoryTile } from "@/components/categories/AddCategoryTile";
import { CategoryTypeTabs } from "@/components/categories/CategoryTypeTabs";
import { useCurrency } from "@/contexts/CurrencyContext";

type CategoryWithBudget = {
  id: string;
  name: string;
  color: string;
  type: "expense" | "income";
  monthlyBudgetLimit?: number | null;
};

export default function Categories() {
  const {
    categories,
    transactions,
    addCategory,
    updateCategory,
    deleteCategory,
    loadTransactions,
    loading,
  } = useExpense();
  const { currency } = useCurrency();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isHe = i18n.language === "he";

  const [isOpen, setIsOpen] = useState(false);
  const [activeType, setActiveType] = useState<"expense" | "income">("expense");
  const [editingCategory, setEditingCategory] = useState<CategoryWithBudget | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<{ id: string; name: string } | null>(null);
  const [formData, setFormData] = useState({
    name: "",
    color: getRandomColor(),
    type: "expense" as "expense" | "income",
    monthlyBudgetLimit: "" as string,
  });

  useEffect(() => {
    const from = format(startOfMonth(new Date()), "yyyy-MM-dd");
    const to = format(endOfMonth(new Date()), "yyyy-MM-dd");
    void loadTransactions({ from, to });
  }, [loadTransactions]);

  const typedCategories = categories as unknown as CategoryWithBudget[];
  const expenseCount = typedCategories.filter((c) => c.type === "expense").length;
  const incomeCount = typedCategories.filter((c) => c.type === "income").length;

  const categoryCards = useMemo<CategoryCardData[]>(() => {
    return typedCategories
      .filter((c) => c.type === activeType)
      .map((category) => {
        const spent = transactions
          .filter((tx) => tx.categoryId === category.id || tx.category === category.name)
          .reduce((sum, tx) => sum + Math.abs(Number(tx.amount) || 0), 0);

        return {
          id: category.id,
          name: category.name,
          color: category.color,
          type: category.type,
          budget: category.monthlyBudgetLimit ?? null,
          spent,
        };
      });
  }, [activeType, typedCategories, transactions]);

  const resetForm = () => {
    setFormData({ name: "", color: getRandomColor(), type: "expense", monthlyBudgetLimit: "" });
    setEditingCategory(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error(t("messages.categoryNameRequired"));
      return;
    }

    try {
      const payload = {
        name: formData.name,
        color: formData.color,
        type: formData.type,
        monthlyBudgetLimit: formData.monthlyBudgetLimit ? parseFloat(formData.monthlyBudgetLimit) : null,
      };
      if (editingCategory) {
        await updateCategory(editingCategory.id, payload);
        toast.success(t("messages.categoryUpdated"));
      } else {
        await addCategory(payload);
        toast.success(t("messages.categoryAdded"));
      }
      setIsOpen(false);
      resetForm();
    } catch {
      toast.error(isHe ? "שמירה נכשלה" : "Failed to save category");
    }
  };

  const handleEdit = (category: CategoryWithBudget) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      color: category.color,
      type: category.type || "expense",
      monthlyBudgetLimit: category.monthlyBudgetLimit?.toString() || "",
    });
    setIsOpen(true);
  };

  const openMore = (category: CategoryWithBudget) => {
    setCategoryToDelete({ id: category.id, name: category.name });
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!categoryToDelete) return;
    try {
      await deleteCategory(categoryToDelete.id);
      toast.success(t("messages.categoryDeleted"));
    } catch {
      toast.error(isHe ? "מחיקה נכשלה" : "Failed to delete category");
    } finally {
      setDeleteDialogOpen(false);
      setCategoryToDelete(null);
    }
  };

  const copy = {
    title: isHe ? "קטגוריות" : "Categories",
    subtitle: isHe
      ? "ארגן את הכסף שלך לפי קטגוריות וקבע תקציב לכל אחת"
      : "Organize your money and set a monthly budget per category",
    newCategory: isHe ? "קטגוריה חדשה" : "New category",
  };

  return (
    <div className="page-shell" dir={isHe ? "rtl" : "ltr"}>
      <div className="mb-6 flex items-start justify-between gap-6">
        <div>
          <h2 className="text-[22px] font-bold tracking-[-0.015em] text-[var(--text-primary)] sm:text-[26px]">{copy.title}</h2>
          <p className="mt-1 text-[14px] text-[var(--text-muted)]">{copy.subtitle}</p>
        </div>
        <Dialog
          open={isOpen}
          onOpenChange={(open) => {
            setIsOpen(open);
            if (!open) resetForm();
          }}
        >
          <DialogTrigger asChild>
            <Button className="inline-flex items-center gap-2 rounded-[8px] bg-[var(--fg-accent)] px-[14px] py-[9px] text-[13px] font-semibold text-white hover:bg-[var(--fg-accent-hover)]">
              <Plus className="h-[14px] w-[14px]" />
              {copy.newCategory}
            </Button>
          </DialogTrigger>

          <DialogContent dir={isHe ? "rtl" : "ltr"} className="shadow-[0_16px_40px_rgba(0,0,0,0.45)]">
            <DialogHeader>
              <DialogTitle>{editingCategory ? t("categories.editCategory") : t("categories.addCategory")}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">{t("categories.categoryName")}</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                />
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>{t("categories.categoryType")}</Label>
                  <ToggleGroup
                    type="single"
                    value={formData.type}
                    onValueChange={(value) =>
                      value && setFormData((prev) => ({ ...prev, type: value as "expense" | "income" }))
                    }
                  >
                    <ToggleGroupItem value="expense" aria-label={t("categories.expense")} className="gap-2">
                      <TrendingDown className="h-4 w-4" />
                      {t("categories.expense")}
                    </ToggleGroupItem>
                    <ToggleGroupItem value="income" aria-label={t("categories.income")} className="gap-2">
                      <TrendingUp className="h-4 w-4" />
                      {t("categories.income")}
                    </ToggleGroupItem>
                  </ToggleGroup>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="monthlyBudgetLimit" className="inline-flex items-center gap-2">
                    <Target className="h-4 w-4 text-amber-500" />
                    {t("categories.monthlyBudget")}
                  </Label>
                  <Input
                    id="monthlyBudgetLimit"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.monthlyBudgetLimit}
                    onChange={(e) => setFormData((prev) => ({ ...prev, monthlyBudgetLimit: e.target.value }))}
                    disabled={formData.type !== "expense"}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>{t("categories.categoryColor")}</Label>
                <div className="grid grid-cols-5 gap-1.5">
                  {colorOptions.map((color) => (
                    <button
                      key={color}
                      type="button"
                      className={`h-8 w-8 rounded-full border-2 transition-all ${
                        formData.color === color ? "scale-110 border-foreground" : "border-muted-foreground/30"
                      }`}
                      style={{ backgroundColor: color }}
                      onClick={() => setFormData((prev) => ({ ...prev, color }))}
                    />
                  ))}
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsOpen(false)} disabled={loading}>
                  {t("categories.cancel")}
                </Button>
                <Button type="submit" disabled={loading || !formData.name.trim()}>
                  {loading ? "Saving..." : t("categories.save")}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mb-5">
        <CategoryTypeTabs
          value={activeType}
          onChange={setActiveType}
          expenseCount={expenseCount}
          incomeCount={incomeCount}
          isHe={isHe}
        />
      </div>

      <div className="grid grid-cols-[repeat(auto-fill,minmax(280px,1fr))] gap-4">
        {categoryCards.map((card) => {
          const source = typedCategories.find((c) => c.id === card.id);
          if (!source) return null;
          return (
            <CategoryCard
              key={card.id}
              category={card}
              currency={currency.symbol || "₪"}
              isHe={isHe}
              onOpen={(categoryName) => navigate(`/transactions?category=${encodeURIComponent(categoryName)}`)}
              onEdit={() => handleEdit(source)}
              onMore={() => openMore(source)}
            />
          );
        })}
        <AddCategoryTile
          isHe={isHe}
          onClick={() => {
            resetForm();
            setIsOpen(true);
          }}
        />
      </div>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent dir={isHe ? "rtl" : "ltr"}>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("categories.deleteTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("categories.deleteDescription", { name: categoryToDelete?.name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className={isHe ? "flex-row-reverse gap-2" : ""}>
            <AlertDialogCancel onClick={() => setCategoryToDelete(null)}>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {t("common.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}